import React from 'react';
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {useParams} from "react-router-dom";
import moment from "moment/moment";
import {concepts, SignUpCode} from "fearless-common";
import {Alert, Button } from "fearless-common-ui";
import {adminRoutes, newString} from "../../utils/routes";
import Pages from "../../components/layout/Pages";
import useGet from "../../components/hooks/useGet";
import {defaultDateTimeFormat} from "../../utils/date";
import Loading from "../../components/Loading";
import FloatingLabelField from "../../components/forms/FloatingLabelField";
import {BackendForm} from "../../components/forms";
import AdminNotes from "../../components/admin/AdminNotes";
import useTitle from "../../components/hooks/useTitle";
import {backendEndpoints} from "../../libs/backendEndpoints";

/**
 * Separate view containing the form for cleaner code.
 */
function InnerAdminSignUpCode({ signUpCode, isNew }: { signUpCode?: SignUpCode; isNew: boolean }) {
    useTitle(isNew ? `New ${concepts.SignUpCode}` : `Admin ${concepts.SignUpCode}: ${signUpCode?.code}`);
    return (
        <Row>
            <Col>
                <BackendForm<Omit<SignUpCode, 'id' | 'created_at' | 'updated_at' | 'created_by_id'>, { id: number }>
                    initialValues={signUpCode || {
                        code: '',
                        title: '',
                        number_of_months: 6,
                        enticing_message: '',
                        valid_until: '',
                    }}
                    targetEndpoint={!signUpCode ? backendEndpoints.createAdminSignUpCode : backendEndpoints.getAdminSignUpCode(signUpCode.id)}
                    targetMethod={isNew ? 'POST' : 'PUT'}
                    onSuccess={({ data}) => {
                      window.location.href = data?.id ? adminRoutes.signUpCode(data.id) : adminRoutes.signUpCodes;
                    }}
                >
                    <FloatingLabelField name="title" label="Title" required helperText="Description so we know the context of the code" />
                    <FloatingLabelField name="code" label="Code" required />
                    <FloatingLabelField name="number_of_months" type="number" label="Number of extra trials months" required />
                    <FloatingLabelField
                      name="enticing_message"
                      label="Enticing message"
                      helperText="Message that shows on the sign up page if the code was succesfully validated"
                    />
                    <FloatingLabelField name="valid_until" type="date" label="Valid until" required={false} helperText="In date format YYYY-MM-DD" />
                </BackendForm>
            </Col>
            <Col>
                {!isNew && signUpCode ? (
                    <AdminNotes type="App\Models\SignUpCode" typeId={signUpCode.id} />
                ) : null}
            </Col>
        </Row>
    );
}

/**
 * Show a form to edit a sign-up code
 */
export default function AdminSignUpCode() {
    const params = useParams();
    const { signUpCodeId } = params;
    const isNew = signUpCodeId === newString;
    const {
        data: signUpCode,
        error,
        isLoading,
    } = useGet<SignUpCode>({
        endpoint: isNew ? '' : `/admin/sign-up-codes/${signUpCodeId}`,
    });

    return (
        <Pages.Centred>
            <Button variant="primary" icon="fa fa-arrow-left" className="mb-2" href={adminRoutes.signUpCodes}>
              Back to {concepts.SignUpCode}
            </Button>
            <h1>{isNew ? `New ${concepts.SignUpCode}`: `${concepts.SignUpCode}: ${signUpCode?.code}`}</h1>
            <div className="d-flex gap-4 mb-3">
                <span>Created at: {moment(signUpCode?.created_at).format(defaultDateTimeFormat)}</span>
                <span>Last updated: {moment(signUpCode?.updated_at).format(defaultDateTimeFormat)}</span>
            </div>
            {error && <Alert>{error}</Alert>}
            {isLoading && <Loading />}
            {(signUpCode || isNew) && (
                <InnerAdminSignUpCode signUpCode={signUpCode} isNew={isNew} />
            )}
        </Pages.Centred>
    );
}
